/**
 * Google from the event's page: the directions links, which open Google Maps.
 * The maps drawn in the page are ours (`src/lib/maps.ts`), and so is a trail's
 * route picture once admin-api has drawn it (OSM plan phase 2); until then the
 * route is a Static Maps image, the last use of the key.
 */
import type { Point } from "@/lib/types/event";

// a browser key, restricted by referrer, so public by design; Static Maps only,
// for a route admin-api has not drawn yet
const STATIC_MAPS_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY || "AIzaSyCiN7J_YxlrStj9U53tpamDS9iyhns5Wpg";

/** a link typed in by hand, only if it is one a browser should follow */
export const safeHref = (href: string | null | undefined) => {
  const text = String(href || "").trim();
  return /^https?:\/\//i.test(text) ? text : "";
};

/** directions to a point; every POINT is x = longitude, y = latitude */
export const directionsUrl = (location: Point | null | undefined) =>
  location && location.x && location.y ? `https://www.google.com/maps/dir/?api=1&destination=${location.y},${location.x}` : "";

/**
 * Where to open for a place: the link staff gave, or directions to its
 * coordinates. Nothing for a place with neither, which is everywhere on the
 * public programme, since it is not given them.
 */
export const mapsHref = (link: string | null | undefined, location: Point | null | undefined) => {
  const given = safeHref(link);
  if (given) {
    return given;
  }
  return directionsUrl(location);
};

// The route in red, the colour a hiking route is drawn in, and the one that
// stands out against a terrain map's greens. The way Static Maps takes a
// colour: 0xRRGGBBAA
const ROUTE_COLOUR = "0xdc2626ff";
const ROUTE_WEIGHT = 4;

type Routed = { route?: string | null; route_map?: { path: string; url?: string | null } | null };

const CDN = "https://cdn.montanhasdentro.pt/";

/**
 * A trail's route as a picture: ours, drawn by the back-office on our map and
 * kept on the CDN (`route_map`), or, while a route has none that is current, Google's Static Maps
 * image of the route the API sends already simplified and encoded, so the
 * address stays short. 640 is as big as Static Maps draws, doubled by scale for
 * a sharp image. Nothing for a trail with no route.
 */
export const routeImageUrl = (trail: Routed | null | undefined) => {
  // ours, which public-api sends only while it shows the route as it is now;
  // without these four lines every route is Google's again
  const drawn = trail?.route_map;
  if (drawn && (drawn.url || drawn.path)) {
    return safeHref(drawn.url) || `${CDN}${drawn.path}`;
  }
  const route = trail?.route;
  if (!route || !STATIC_MAPS_KEY) {
    return "";
  }
  const params = new URLSearchParams({
    size: "640x400",
    scale: "2",
    maptype: "terrain",
    path: `color:${ROUTE_COLOUR}|weight:${ROUTE_WEIGHT}|enc:${route}`,
    key: STATIC_MAPS_KEY,
  });
  return `https://maps.googleapis.com/maps/api/staticmap?${params}`;
};
